import { Controller, UseFormReturn } from 'react-hook-form';

import get from 'lodash/get';
import { ArrowRightIcon } from 'lucide-react';

import { DatePicker } from 'components/DatePicker';
import { Label } from 'components/Label';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { useExtendedFormContext } from 'contexts/extendedFormContext';

import { QuickQuoteLocationInput } from '../common/QuickQuoteLocationInput';
import { QuickQuoteInputs } from '../types';

interface StopsSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  hasAddedQuoteDates: boolean;
  showDatesAboveStops?: boolean;
}

export const StopsSection = ({
  formMethods,
  hasAddedQuoteDates,
  showDatesAboveStops = false,
}: StopsSectionProps) => {
  const { control, formState, getFieldState } = formMethods;
  const { defaultValues, dirtyFields } = formState;

  const { aiDefaultValues } = useExtendedFormContext();

  const hasAIHint = (fieldName: string): boolean => {
    const defaultValue = get(defaultValues, fieldName);
    const fieldState = getFieldState(fieldName as any);
    const isValid = !fieldState.isDirty && !get(dirtyFields, fieldName);

    return !!(aiDefaultValues && defaultValue && isValid);
  };

  const pickupHasAIHint = hasAIHint('stops.0.location');
  const dropoffHasAIHint = hasAIHint('stops.1.location');

  return (
    <>
      {showDatesAboveStops ? (
        <Flex direction='col' gap='sm' className='w-full mx-0'>
          <div className='text-md text-neutral-700 font-semibold'>Pickup</div>

          <Grid cols='5' gap='xs' className='w-full mx-0'>
            <div className='w-full col-span-3'>
              <QuickQuoteLocationInput
                name='stops.0.location'
                label='Location'
                placeholder='City, State or ZIP'
                inputClassName='placeholder:text-[12px]'
                required
              />
            </div>
            <Flex direction='col' className='w-full col-span-2'>
              <Label name='pickupDate' aiIconOnly required={false}>
                Date
                <span className='ml-1 text-xs text-neutral-600'>
                  (optional)
                </span>
              </Label>
              <Controller
                name='pickupDate'
                control={control}
                render={({ field }) => (
                  <DatePicker
                    field={field}
                    className='mt-1 pl-2'
                    calendarClassName='mr-1.5'
                  />
                )}
              />
            </Flex>
          </Grid>

          <div className='text-md text-neutral-700 font-semibold mt-3'>
            Dropoff
          </div>

          <Grid cols='5' gap='xs' className='w-full mx-0'>
            <div className='w-full col-span-3'>
              <QuickQuoteLocationInput
                name='stops.1.location'
                label='Location'
                placeholder='City, State or ZIP'
                inputClassName='placeholder:text-[12px]'
                required
              />
            </div>
            <Flex direction='col' className='w-full col-span-2'>
              <Label name='deliveryDate' aiIconOnly required={false}>
                Date
                <span className='ml-1 text-xs text-neutral-600'>
                  (optional)
                </span>
              </Label>
              <Controller
                name='deliveryDate'
                control={control}
                render={({ field }) => (
                  <DatePicker
                    field={field}
                    className='mt-1 pl-2'
                    calendarClassName='mr-1.5'
                  />
                )}
              />
            </Flex>
          </Grid>
        </Flex>
      ) : (
        <>
          <Flex
            justify='between'
            align='start'
            gap='sm'
            className='w-full mx-0'
          >
            <div className='w-full'>
              <QuickQuoteLocationInput
                name='stops.0.location'
                label='Pickup'
                placeholder='City, State or ZIP'
                inputClassName='placeholder:text-[12px]'
                required
              />
            </div>

            {!pickupHasAIHint && !dropoffHasAIHint && (
              <ArrowRightIcon className='w-10 text-neutral-500 mt-7' />
            )}

            <div className='w-full'>
              <QuickQuoteLocationInput
                name='stops.1.location'
                label='Dropoff'
                placeholder='City, State or ZIP'
                inputClassName='placeholder:text-[12px]'
                required
              />
            </div>
          </Flex>

          {hasAddedQuoteDates && (
            <Flex direction='col' gap='md'>
              <Typography variant='body' weight='semibold'>
                Dates
              </Typography>
              <Flex
                justify='between'
                align='end'
                gap='lg'
                className='w-full mx-0'
              >
                <div className='w-full'>
                  <Label name='pickupDate'>Pickup</Label>
                  <Controller
                    name={`pickupDate`}
                    control={control}
                    render={({ field }) => <DatePicker field={field} />}
                  />
                </div>

                <div className='w-full'>
                  <Label name='deliveryDate'>Dropoff</Label>
                  <Controller
                    name={`deliveryDate`}
                    control={control}
                    render={({ field }) => <DatePicker field={field} />}
                  />
                </div>
              </Flex>
            </Flex>
          )}
        </>
      )}
    </>
  );
};
